import React, { useState, useEffect } from 'react';

const ServiceFormPage = ({ serviceId, onSave, onCancel }) => {
  const [service, setService] = useState({
    name: '',
    description: '',
    unit: '',
    price: '',
    currency: 'RON',
    cpvCode: ''
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const isEditing = Boolean(serviceId);

  const currencies = [
    { value: 'RON', label: 'RON' },
    { value: 'EUR', label: 'EUR' },
    { value: 'USD', label: 'USD' }
  ];

  const commonUnits = [
    'buc', 'ore', 'luni', 'serviciu', 'pachet', 'licență', 'consultatii', 'proiect'
  ];

  useEffect(() => {
    if (isEditing) {
      fetchService();
    }
  }, [serviceId]);

  const fetchService = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/services/${serviceId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setService(data);
      } else {
        throw new Error('Eroare la încărcarea serviciului');
      }
    } catch (error) {
      console.error('Error fetching service:', error);
      alert('Eroare la încărcarea datelor serviciului');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setService(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!service.name.trim()) {
      newErrors.name = 'Numele serviciului este obligatoriu';
    }

    if (!service.price || parseFloat(service.price) < 0) {
      newErrors.price = 'Prețul trebuie să fie un număr pozitiv';
    }

    if (service.cpvCode && !/^[0-9]{8}(-[0-9])?$/.test(service.cpvCode)) {
      newErrors.cpvCode = 'Formatul codului CPV este invalid (ex: 12345678 sau 12345678-1)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const url = isEditing ? `/api/services/${serviceId}` : '/api/services';
      const method = isEditing ? 'PUT' : 'POST';

      const serviceData = {
        ...service,
        price: parseFloat(service.price)
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(serviceData),
      });

      if (response.ok) {
        const savedService = await response.json();
        alert(isEditing ? 'Serviciu actualizat cu succes!' : 'Serviciu creat cu succes!');
        if (onSave) {
          onSave(savedService);
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Eroare la salvarea serviciului');
      }
    } catch (error) {
      console.error('Error saving service:', error);
      alert('Eroare la salvarea serviciului: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      window.history.back();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Se încarcă...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {isEditing ? 'Editare Serviciu' : 'Serviciu Nou'}
              </h1>
              <p className="text-gray-600 mt-1">
                {isEditing ? 'Modificați informațiile serviciului' : 'Completați informațiile pentru serviciul nou'}
              </p>
            </div>
            <button
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Form */}
        <div className="space-y-6">
          {/* Informații serviciu */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Informații Serviciu</h2>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nume Serviciu *
                </label>
                <input
                  type="text"
                  name="name"
                  value={service.name}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Nume serviciu"
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descriere
                </label>
                <textarea
                  name="description"
                  value={service.description}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Descrierea detaliată a serviciului"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Unitate de Măsură
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      name="unit"
                      value={service.unit}
                      onChange={handleInputChange}
                      list="units"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="buc, ore, luni, etc."
                    />
                    <datalist id="units">
                      {commonUnits.map(unit => (
                        <option key={unit} value={unit} />
                      ))}
                    </datalist>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cod CPV
                  </label>
                  <input
                    type="text"
                    name="cpvCode"
                    value={service.cpvCode}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.cpvCode ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="12345678"
                  />
                  {errors.cpvCode && <p className="text-red-500 text-sm mt-1">{errors.cpvCode}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preț *
                  </label>
                  <input
                    type="number"
                    name="price"
                    value={service.price}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.price ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="0.00"
                  />
                  {errors.price && <p className="text-red-500 text-sm mt-1">{errors.price}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Monedă
                  </label>
                  <select
                    name="currency"
                    value={service.currency}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {currencies.map(currency => (
                      <option key={currency.value} value={currency.value}>
                        {currency.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Preview</h2>
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <h3 className="font-medium text-gray-900">{service.name || 'Nume serviciu'}</h3>
              {service.description && (
                <p className="text-sm text-gray-600 mt-1">{service.description}</p>
              )}
              <div className="flex justify-between items-center mt-3">
                <span className="text-sm text-gray-500">
                  {service.unit || 'U.M.'} {service.cpvCode && `• CPV: ${service.cpvCode}`}
                </span>
                <span className="font-semibold text-blue-600">
                  {service.price ? `${parseFloat(service.price).toFixed(2)} ${service.currency}` : '0.00 RON'}
                </span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={handleCancel}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
              >
                Anulează
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {saving && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                )}
                {saving ? 'Se salvează...' : (isEditing ? 'Actualizează' : 'Creează')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceFormPage;